export type MonitoringCategoryValue = string | number;
export type MonitoringCategories = Record<string, MonitoringCategoryValue>;
export type MonitoringMetrics = Record<string, number>;

const MAX_CATEGORY_VALUE_LENGTH = 64;
const EMAIL_PATTERN = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const ALLOWED_CATEGORY_KEYS = new Set([
  'api_name',
  'result',
  'code',
  'stage',
  'fallback',
  'priority',
  'dependency',
  'locale',
  'project_id',
  'region',
  'is_boe',
  'captcha_initialized',
  'captcha_status',
  'idc_name',
  'error_type',
  'page',
  'channel',
  'has_root'
]);
const ALLOWED_METRIC_KEYS = new Set(['duration_ms', 'root_child_count', 'root_text_length']);

const normalizeString = (value: string): string =>
  value
    .replace(/https?:\/\/\S+/gi, '[url]')
    .replace(EMAIL_PATTERN, '[email]')
    .replace(/[?#][^\s]*/g, '')
    .slice(0, MAX_CATEGORY_VALUE_LENGTH);

export const normalizeErrorType = (error: unknown): string => {
  if (error instanceof Error && error.name) {
    return normalizeString(error.name);
  }

  if (error && typeof error === 'object') {
    const name = (error as { name?: unknown }).name;
    if (typeof name === 'string' && name) {
      return normalizeString(name);
    }
    return 'object_error';
  }

  return `${typeof error}_error`;
};

export const normalizeCode = (code: unknown): string | undefined => {
  if (typeof code === 'number' && Number.isFinite(code)) {
    return String(code);
  }
  if (typeof code === 'string' && code) {
    return normalizeString(code);
  }
  return undefined;
};

export const sanitizeCategories = (input: Record<string, unknown>): MonitoringCategories => {
  const categories: MonitoringCategories = {};

  Object.entries(input).forEach(([key, value]) => {
    if (!ALLOWED_CATEGORY_KEYS.has(key) || value === undefined || value === null) {
      return;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      categories[key] = value;
      return;
    }

    if (typeof value === 'boolean') {
      categories[key] = value ? 1 : 0;
      return;
    }

    if (typeof value === 'string') {
      categories[key] = normalizeString(value);
    }
  });

  return categories;
};

export const sanitizeMetrics = (input: Record<string, unknown> = {}): MonitoringMetrics => {
  const metrics: MonitoringMetrics = {};

  Object.entries(input).forEach(([key, value]) => {
    if (!ALLOWED_METRIC_KEYS.has(key) || typeof value !== 'number' || !Number.isFinite(value)) {
      return;
    }
    metrics[key] = Math.max(0, Math.round(value));
  });

  return metrics;
};

export const getDurationMs = (startedAt: number, endedAt = Date.now()): number =>
  Math.max(0, Math.round(endedAt - startedAt));
